import { React, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Breadcrumbs, Typography, Box, Chip } from '@mui/material';
import { useCategories } from '../context/CategoriesContext';
import { findCategoryInTree, getAllCategoryIdsFromTree } from '../utils/categoryUtils';
import Link from '@mui/material/Link';
import LoadingComponent from "../components/LoadingComponent"

/**
 * CategoryMenu component displays a breadcrumb navigation for category selection
 * @param {Object} props - Component props
 * @param {Function} props.onCategorySelect - Callback when selection changes: (selectedId: number|null, allCategoryIds: number[])
 * @param {number|null} props.startingPointId - Initial category ID to select (optional)
 * @param {Function} props.changeStartingPoint - Callback to reset startingPointId after it's been processed
 * @param {Object} props.externalCategoryCountMap - Map of category IDs to listing counts (optional)
 * @param {boolean} props.isCreateListing - Whether this component is used in the CreateListing page (optional)
 */
export default function CategoryMenu({ onCategorySelect, startingPointId = null, changeStartingPoint = null, externalCategoryCountMap = null, isCreateListing = false }) {

  // Track if we've processed the startingPointId
  const hasProcessedStartingPoint = useRef(false);
  const lastStartingPointIdRef = useRef(null);

  // Fetch categories from API
  const { categories, isLoading } = useCategories();

  // Menu state containing root categories, breadcrumb path, children of selected category, and selected category
  const [menu, setMenu] = useState({
    root: [],           // Root categories
    breadcrumb: [],     // Path to selected category (for breadcrumb navigation)
    children: [],       // Children of the selected category
    selected: {}        // Currently selected category (object for UI)
  });

  // Reset function to clear the menu state
  const resetMenu = useCallback(() => {
    if (categories && categories.length > 0) {
      const rootMenu = Array.isArray(categories) ? categories : [categories];
      setMenu({
        root: rootMenu,
        breadcrumb: [],
        children: rootMenu,
        selected: {}
      });

      // Notify parent that no category is selected
      onCategorySelect(null, []);
    }
  }, [categories, onCategorySelect]);

  // Update menu when categories are loaded - only on initial load
  useEffect(() => {
    if (categories && !isLoading && !hasProcessedStartingPoint.current) {
      const rootMenu = Array.isArray(categories) ? categories : [categories];
      setMenu({
        root: rootMenu,
        breadcrumb: [],
        children: rootMenu,
        selected: {}
      });
    }
  }, [categories, isLoading, hasProcessedStartingPoint]);

  const lastNotifiedSelectionRef = useRef({ categoryId: undefined, allIdsKey: '' });

  // Notify parent component when selected category changes (selectedId, allCategoryIds)
  useEffect(() => {
    const selectedId = menu?.selected?.id ?? null;
        const allIds = selectedId != null && categories?.length
          ? getAllCategoryIdsFromTree(Array.isArray(categories) ? categories : [categories], selectedId)
          : [];
    // Before applying startingPointId, menu.selected is still empty — do not notify parent as "cleared",
    // or Search will wipe URL-derived categories before the restore effect runs (new tab / shared link).
    if (
      selectedId == null &&
      startingPointId != null &&
      !hasProcessedStartingPoint.current
    ) {
      return;
    }
    const allIdsKey = allIds.map(String).sort((a, b) => Number(a) - Number(b)).join(',');
    if (
      lastNotifiedSelectionRef.current.categoryId === selectedId &&
      lastNotifiedSelectionRef.current.allIdsKey === allIdsKey
    ) {
      return;
    }
    lastNotifiedSelectionRef.current = { categoryId: selectedId, allIdsKey };
    onCategorySelect(selectedId, allIds);
  }, [menu.selected, onCategorySelect, categories, startingPointId]);

  /**
   * Finds the path from root to a category by its ID
   * @param {Array} categories - Array of category objects to search through
   * @param {number} categoryId - ID of the category to find
   * @param {Array} path - Current path (used in recursion)
   * @returns {Array|null} - Array of categories forming the path, or null if not found
   */
  const findCategoryPath = useCallback((categories, categoryId, path = []) => {
    if (!categories || !categoryId) return null;

    for (const category of categories) {
      const newPath = [...path, category];
      if (category.id === categoryId) {
        return newPath;
      }
      if (category.children && category.children.length > 0) {
        const childPath = findCategoryPath(category.children, categoryId, newPath);
        if (childPath) {
          return childPath;
        }
      }
    }
    return null;
  }, []);

  /**
   * Collects all descendant categories (children, grandchildren, etc.)
   * @param {Object} parentCategory - The parent category object
   * @returns {Array} - Array of all descendant categories
   */
  const getAllDescendants = useCallback((parentCategory) => {
    if (!parentCategory || !parentCategory.children) return [];

    let descendants = [...parentCategory.children];

    for (const child of parentCategory.children) {
      if (child.children) {
        descendants = descendants.concat(getAllDescendants(child));
      }
    }

    return descendants;
  }, []);

  /**
   * Handles click on a category in the breadcrumb navigation
   * @param {Object} category - The clicked category
   */
  const handleParentCategoryClick = useCallback((category) => {
    if (!category || !category.id) return;

    // Find path from root to the selected category
    const path = findCategoryPath(menu.root, category.id);
    if (!path) return;

    // Check if we're clicking on a root category that's already selected
    const isRootCategory = menu.root.some(rootCat => rootCat.id === category.id);
    const isAlreadySelected = menu?.selected?.id === category.id;
    const isDeselecting = isRootCategory && isAlreadySelected;

    // Determine the new state based on the clicked category
    let newBreadcrumb = [];
    let newChildren = [];

    if (isDeselecting) {
      // If deselecting a root category, reset to root view
      newChildren = menu.root;
    } else {
      // Set breadcrumb to the path to the selected category
      newBreadcrumb = path;

      // Set children to the children of the selected category
      newChildren = category.children && category.children.length > 0
        ? category.children
        : []; // Empty array if no children
    }

    // Update menu state
    setMenu({
      root: [...menu.root],
      breadcrumb: newBreadcrumb,
      children: newChildren,
      selected: isDeselecting ? {} : category
    });
  }, [menu.root, menu.selected, findCategoryPath]);



  // Handle startingPointId changes
  useEffect(() => {

    // Reset hasProcessedStartingPoint if startingPointId changed to a different category
    if (startingPointId != null) {
      if (lastStartingPointIdRef.current !== startingPointId) {
        hasProcessedStartingPoint.current = false;
        lastStartingPointIdRef.current = startingPointId;
      }
    } else {
      if (hasProcessedStartingPoint.current) {
        hasProcessedStartingPoint.current = false;
      }
      lastStartingPointIdRef.current = null;
    }

    // Only process if we have a startingPointId and categories are loaded
    if (startingPointId != null && menu.root.length > 0 && !hasProcessedStartingPoint.current) {
      hasProcessedStartingPoint.current = true;

      const categoryToSelect = findCategoryInTree(menu.root, startingPointId);

      if (categoryToSelect) {
        const path = findCategoryPath(menu.root, categoryToSelect.id);

        if (path) {
          setMenu({
            root: [...menu.root],
            breadcrumb: path,
            children: categoryToSelect.children && categoryToSelect.children.length > 0
              ? categoryToSelect.children
              : [],
            selected: categoryToSelect
          });
        } else {
          handleParentCategoryClick(categoryToSelect);
        }
      }

      if (changeStartingPoint) {
        changeStartingPoint();
      }
    }
  }, [startingPointId, menu.root, changeStartingPoint, handleParentCategoryClick, findCategoryPath]);

  /**
   * Renders the breadcrumb navigation menu
   */
  const renderCategoryMenu = useMemo(() => {

    // Show loading indicator while categories are being fetched
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LoadingComponent/>
          <Typography variant="body2">Ładowanie kategorii...</Typography>
        </Box>
      );
    }

    // Show empty state if no categories are available
    if ((!menu.children || menu.children.length === 0) && (!menu.breadcrumb || menu.breadcrumb.length === 0)) {
      return <Typography variant="body2">Brak dostępnych kategorii</Typography>;
    }

    return (
      <Box>
        {/* Breadcrumb navigation showing the path */}
        {menu.breadcrumb.length > 0 && (
          <Breadcrumbs maxItems={999} aria-label="ścieżka kategorii" sx={{ mb: 1 }}>
            {/* Add Home/Root link */}
            <Link
              id="category-menu-home"
              underline="hover"
              color="inherit"
              onClick={resetMenu}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  resetMenu();
                }
              }}
              tabIndex={0}
              role="button"
              sx={{
                color: "text.secondary",
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                '&:focus': { outline: '1px solid', outlineColor: 'primary.main' }
              }}
              variant="subtitle1"
            >
              Główna
            </Link>

            {/* Render breadcrumb items */}
            {menu.breadcrumb.map((item) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }} key={item.id}>
                <Link
                  id={`breadcrumb-item-${item.id}`}
                  underline="hover"
                  color="inherit"
                  onClick={() => handleParentCategoryClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleParentCategoryClick(item);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-current={menu.selected?.id === item.id ? 'page' : undefined}
                  sx={{
                    color: "text.secondary",
                    fontWeight: menu.selected?.id === item.id ? 'fontWeightBold' : 'normal',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    '&:focus': { outline: '1px solid', outlineColor: 'primary.main' }
                  }}
                  variant="subtitle1"
                >
                  {item.name}
                </Link>
                {externalCategoryCountMap && (
                  <Chip
                    size="small"
                    label={externalCategoryCountMap[item.id] || 0}
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: externalCategoryCountMap[item.id] > 0 ? 'primary.light' : 'grey.300',
                      color: externalCategoryCountMap[item.id] > 0 ? 'white' : 'text.secondary'
                    }}
                  />
                )}
              </Box>
            ))}
          </Breadcrumbs>
        )}

        {/* Category children list */}
        <Breadcrumbs maxItems={999} aria-label="kategorie">
          {menu.children.map((item) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }} key={item.id}>
              <Link
                id={`category-item-${item.id}`}
                underline="hover"
                color="inherit"
                onClick={() => handleParentCategoryClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleParentCategoryClick(item);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-current={menu.selected?.id === item.id ? 'page' : undefined}
                sx={{
                  maxWidth: 220,
                  color: "text.secondary",
                  fontWeight: menu.selected?.id === item.id ? 'fontWeightBold' : 'normal',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus': { outline: '1px solid', outlineColor: 'primary.main' }
                }}
                variant="subtitle1"
              >
                {item.name}
                {isCreateListing && (!item.children || item.children.length === 0) && (
                  <Box component="span" sx={{ ml: 1, color: 'success.main', fontSize: '0.8rem' }}>
                    ✓
                  </Box>
                )}
              </Link>
              {externalCategoryCountMap && (
                <Chip
                  size="small"
                  label={externalCategoryCountMap[item.id] || 0}
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: externalCategoryCountMap[item.id] > 0 ? 'primary.light' : 'grey.300',
                    color: externalCategoryCountMap[item.id] > 0 ? 'white' : 'text.secondary'
                  }}
                />
              )}
            </Box>
          ))}
        </Breadcrumbs>
      </Box>
    );
  }, [menu.breadcrumb, menu.children, menu.selected, isLoading, handleParentCategoryClick, resetMenu, externalCategoryCountMap, isCreateListing]);

  return (
    <div
      id="category-menu"
      role="navigation"
      aria-label="Nawigacja kategorii"
    >
      {renderCategoryMenu}
    </div>
  );
}
